import { VStack } from '@musinsa/mwms-ds'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useState } from 'react'
import { flushSync } from 'react-dom'
import { GRID_SIZE } from '@/utils/grid/grid'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useSearchCategoryPrices } from '@/services/settlement/settlementPricing/useSettlementPricingQueries'
import { CATEGORY_EXCEL_DOWNLOAD_URL } from '@/services/settlement/settlementPricing/api'
import { CategoryPriceListReq, OrderTypeCode, PriceStatusCode, UnitTypeCode } from '@/services/settlement/settlementPricing/types'
import { CategoryPriceForm } from './CategoryPriceForm'
import { CategoryPriceTable } from './CategoryPriceTable'
import { categoryPriceListSchema, CategoryPriceListSchemaType, getInitForm } from '../../schemas/categoryPriceListSchema'
import { downloadCategoryPriceTemplate } from '../../utils/template'

// 폼 → API 요청.
const toReq = (form: CategoryPriceListSchemaType): CategoryPriceListReq => ({
  country: form.country,
  serviceType: form.serviceType,
  orderType: (form.orderType as OrderTypeCode | null) ?? null,
  unitType: (form.unitType as UnitTypeCode | null) ?? null,
  status: (form.status as PriceStatusCode | null) ?? null,
  logisticsCategoryCodes: form.logisticsCategoryCodes.length ? form.logisticsCategoryCodes : null,
  bizestCategories: form.bizestCategories.length ? form.bizestCategories : null,
  startDate: form.startDate ?? null,
  endDate: form.endDate ?? null,
})

export function CategoryPriceTab() {
  const form = useForm<CategoryPriceListSchemaType>({
    resolver: zodResolver(categoryPriceListSchema),
    defaultValues: getInitForm(),
    mode: 'onChange',
  })

  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(GRID_SIZE.SETTLEMENT)
  const [queryKey, setQueryKey] = useState<CategoryPriceListSchemaType>(form.getValues())

  const { data, refetch, isFetching } = useSearchCategoryPrices({
    ...toReq(queryKey),
    page: currentPage,
    size: pageSize,
  })

  // 카테고리 검색은 country·serviceType 필수(zod min(1)). refetch() 는 이 검증을 우회하므로,
  // 검색 전(필수값 미설정) 상태에서 등록/엑셀 업로드 후 refetch 하면 country:""/serviceType:"" 로
  // BE 400(INVALID_PARAMETER)이 난다(LOGISPRD-9881, 정상 등록되나 에러 토스트). 필수값 있을 때만 갱신, 없으면 스킵.
  const safeRefetch = useCallback(async () => {
    if (!queryKey.country || !queryKey.serviceType) return
    await refetch()
  }, [queryKey.country, queryKey.serviceType, refetch])

  // 엑셀 전체 다운로드 — 현재 검색 조건 매칭 전체 행(.xlsx, 페이지네이션 없음)
  const { onDownloadExcel, isLoading: isDownloading } = useExcelDownload<CategoryPriceListReq>({
    fileNamePrefix: 'MFS_카테고리단가',
    config: { method: 'post' },
  })
  const onDownload = () => onDownloadExcel(CATEGORY_EXCEL_DOWNLOAD_URL, toReq(queryKey))
  // 전체 다운로드는 검색과 동일하게 서비스 항목(serviceType)이 적용된 뒤에만 가능.
  // 미선택 상태(초기 queryKey)로 호출하면 BE 가 serviceType 필수(@NotNull)라 400 → 버튼을 비활성화한다.
  const isDownloadDisabled = !queryKey.serviceType

  // 엑셀 템플릿(업로드 양식) 다운로드 — public 정적 .xlsx 를 클라이언트에서 바로 내려준다.
  // BE GET /category/excel-template(S3 스트리밍)은 에러가 발생(템플릿 부재/권한)하므로 서버 의존을 제거.
  // public/MFS_카테고리단가_업로드양식.xlsx 는 업로드 파서와 1:1 정합되는 실제 양식(헤더 3행/코드표 포함)이라 재업로드 호환.

  const onChangePage = async (page: number) => {
    flushSync(() => setCurrentPage(page))
    await refetch()
  }

  const onChangePageSize = async (size: number) => {
    flushSync(() => {
      setPageSize(size)
      setCurrentPage(0)
    })
    await refetch()
  }

  const handleSearch = async (formData: CategoryPriceListSchemaType) => {
    flushSync(() => {
      setQueryKey(formData)
      setCurrentPage(0)
    })
    await refetch()
  }

  return (
    <FormProvider {...form}>
      <VStack gap={16} style={{ flex: 1, minHeight: 0 }}>
        <CategoryPriceForm onSearch={handleSearch} isLoading={isFetching} />
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <CategoryPriceTable
            refetch={safeRefetch}
            rowData={data?.items ?? []}
            pageInfo={data?.pageInfo ?? { page: 0, size: 0, totalCount: 0, totalPages: 0 }}
            onChangePage={onChangePage}
            onChangePageSize={onChangePageSize}
            onDownload={onDownload}
            onDownloadTemplate={downloadCategoryPriceTemplate}
            isDownloading={isDownloading}
            isDownloadDisabled={isDownloadDisabled}
            isLoading={isFetching}
          />
        </div>
      </VStack>
    </FormProvider>
  )
}
